import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ridesApi } from './rides-api';
import { rideKeys } from './query-keys';
import type { Ride } from './rides-types';

function isActive(r: Ride): boolean {
  return r.status === 'ACTIVE' && r.rideState !== 'RIDE_ENDED' && r.rideState !== 'RIDE_CANCELLED';
}

/**
 * Live order snapshot for a ride. Polls GET /rides/{id} every 5s (reads the latest
 * stored snapshot, updated by unsolicited on_update callbacks) and, as a safety net,
 * forces a fresh on_status from the BPP every ~15s while the ride is active — then
 * the snapshot refetch picks up the result. Stops once the ride is no longer active.
 * `fast` bumps the snapshot poll to 1.5s during the cancel handshake.
 */
export function useRideLive(orderId: string, fast = false) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: rideKeys.detail(orderId),
    queryFn: () => ridesApi.ride(orderId),
    refetchInterval: (q) => {
      if (fast) return 1500;
      const r = q.state.data;
      if (!r) return 5000;
      return isActive(r) ? 5000 : false;
    },
  });

  const ride = query.data;
  const active = ride ? isActive(ride) : false;

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const nudge = () => {
      ridesApi
        .status(orderId)
        .catch(() => {
          /* the BPP may be slow; the 5s snapshot poll still advances the order */
        })
        .finally(() => {
          if (!cancelled) void qc.invalidateQueries({ queryKey: rideKeys.detail(orderId) });
        });
    };
    nudge();
    const interval = setInterval(nudge, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [orderId, active, qc]);

  return {
    ride,
    active,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}

/**
 * Live driver location while the ride is active. Polls GET /rides/{id}/track every ~8s.
 * A 404 (RIDE_005 "no tracking yet") is expected before the first driver ping — the
 * shared queryClient skips 4xx retries and the interval keeps polling, so callers surface
 * "locating driver" (isLocating) until `gps` arrives.
 */
export function useRideTrack(orderId: string, active: boolean) {
  const query = useQuery({
    queryKey: rideKeys.track(orderId),
    queryFn: () => ridesApi.track(orderId),
    enabled: active,
    refetchInterval: active ? 8000 : false,
    retry: false,
  });
  const gps = query.data?.gps;
  return { gps, isLocating: active && !gps };
}
